import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, User, Tag } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const PublicBlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => navigate('/blog'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <SiteHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="blog-detail-page">
      <SiteHeader />
      <SEO title={post.title} description={post.excerpt || post.content?.substring(0, 160)} type="article" />

      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16 relative z-10">
          <button onClick={() => navigate('/blog')} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors" data-testid="back-btn">
            <ArrowLeft size={16} /> Retour au blog
          </button>
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, i) => <span key={i} className="px-3 py-1 bg-white/15 backdrop-blur-sm text-white text-xs font-bold rounded-full">{tag}</span>)}
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="blog-title">
            {post.title}
          </h1>
          {post.excerpt && <p className="text-white/70 text-sm sm:text-base max-w-3xl">{post.excerpt}</p>}
          <div className="flex items-center gap-4 mt-4 text-white/50 text-xs">
            {post.author && <span className="flex items-center gap-1"><User size={14} /> {post.author}</span>}
            {post.created_at && <span className="flex items-center gap-1"><Clock size={14} /> {new Date(post.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
            {post.views !== undefined && <span>{post.views} vue{post.views !== 1 ? 's' : ''}</span>}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f9fafb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {post.thumbnail && (
          <div className="mb-8 rounded-xl overflow-hidden">
            <img src={post.thumbnail} alt={post.title} className="w-full h-auto max-h-[400px] object-cover" data-testid="blog-thumbnail" />
          </div>
        )}
        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100">
          <div className="prose max-w-none text-gray-700 leading-relaxed" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content || '<p>Contenu de l\'article à venir.</p>' }} />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicBlogDetailPage;
